import {
  usePlaybackCurrentTs,
  usePlaybackIsPlaying,
  usePlaybackIsSeeking,
  usePlaybackPause,
  usePlaybackPlay,
  usePlaybackSeekInRange,
  usePlaybackStore,
} from '@app/core/playback/hooks/usePlayback';
import { PlaybackIconBtnV2PlayingAction } from '@mi-front/ui';
import { useCallback } from 'react';

export interface UseRangeWaveformPlaybackArgs {
  from: number;
  to: number;
  /**
   * While playing: `pause` keeps position for resume; `stop` resets to range start.
   * Defaults to `pause`.
   */
  playingAction?: PlaybackIconBtnV2PlayingAction;
  /** Called when playback starts or seeks so sibling players can pause. */
  onPlayStarted?: () => void;
}

/**
 * Play / pause-or-stop / seek helpers for a bounded audio range waveform player.
 * While playing inside [from, to): pause keeps position, stop resets to `from`.
 * Otherwise play/resume within the range (stop always starts from `from`).
 */
export function useRangeWaveformPlayback({
  from,
  to,
  playingAction = 'pause',
  onPlayStarted,
}: UseRangeWaveformPlaybackArgs) {
  const store = usePlaybackStore();
  const play = usePlaybackPlay();
  const pause = usePlaybackPause();
  const isPlaying = usePlaybackIsPlaying();
  const isSeeking = usePlaybackIsSeeking();
  const currentTs = usePlaybackCurrentTs();
  const seekInRange = usePlaybackSeekInRange();

  const isInRange = currentTs != null && currentTs >= from && currentTs < to;
  const showPause = isInRange && isPlaying;

  const togglePlayback = useCallback(() => {
    if (showPause) {
      pause();
      if (playingAction === 'stop') {
        // Snap playhead UI to range start without a media seek; next play seeks to `from`.
        store.setState({ currentTs: from, stopAtTs: null });
      }
      return;
    }

    onPlayStarted?.();
    const startTs =
      playingAction === 'stop' ? from : isInRange && currentTs != null ? currentTs : from;
    void play({ startTs, stopTs: to });
  }, [showPause, pause, playingAction, store, from, onPlayStarted, isInRange, currentTs, play, to]);

  const seek = useCallback(
    (ts: number) => {
      onPlayStarted?.();
      seekInRange(ts);
    },
    [onPlayStarted, seekInRange],
  );

  return {
    showPause,
    /** True while the player loads/seeks after play or seek (segment fetch delay). */
    isSeeking,
    currentTs,
    togglePlayback,
    seek,
  };
}
